import type { PostResumo } from "@/lib/blog.functions";
// Portado de src/pages/tratamentos/emagrecimento.astro (site Astro).
import { Hero } from "@/components/itv/tratamento/Hero";
import { Pilares } from "@/components/itv/tratamento/Pilares";
import { Dores } from "@/components/itv/tratamento/Dores";
import { CardsIlustrado } from "@/components/itv/tratamento/CardsIlustrado";
import { Medico } from "@/components/itv/tratamento/Medico";
import { Faq } from "@/components/itv/tratamento/Faq";
import { Conteudos } from "@/components/itv/tratamento/Conteudos";
import { CtaPratico } from "@/components/itv/tratamento/CtaPratico";

// o-que-faz (Figma 63:32)
const pilares = [
  {
    icone: "/icones/search.svg",
    titulo: "Começa pela investigação",
    texto: "Antes de qualquer dieta, é preciso saber o que está atrapalhando: metabolismo, hormônios, sono ou hábitos.",
  },
  {
    icone: "/icones/shield.svg",
    titulo: "Protege a massa muscular",
    texto: "Perder músculo junto com a gordura facilita o reganho. A composição corporal é medida em todas as reavaliações.",
  },
  {
    icone: "/icones/video-cam.svg",
    titulo: "Onde e como",
    texto: "Presencial em Bauru ou por teleconsulta para todo o Brasil. Atendimento particular.",
    link: { href: "/medico-para-emagrecer-em-bauru/", rotulo: "Atendimento presencial em Bauru" },
  },
];

// dores (Figma 84:40)
const fatores = [
  {
    icone: "/icones/flame.svg",
    titulo: "Metabolismo adaptado",
    texto: "Anos de restrição ensinam o corpo a gastar menos. A pessoa come pouco e mesmo assim não emagrece.",
  },
  {
    icone: "/icones/moon.svg",
    titulo: "Sono e estresse",
    texto: "Dormir mal aumenta a fome e reduz a saciedade. Estresse crônico tem efeito parecido.",
  },
  {
    icone: "/icones/activity.svg",
    titulo: "Medicação usada sozinha",
    texto: "Sem acompanhamento, costuma derrubar massa muscular junto com a gordura, e o peso tende a voltar quando o uso para.",
  },
  {
    icone: "/icones/clipboard.svg",
    titulo: "O que os exames mostram",
    texto: "Resistência à insulina, alterações de tireoide e deficiências passam despercebidas sem os exames certos.",
  },
];

// condutas (Figma 63:94 e 63:104)
const condutas = [
  { img: "/img/emagrecimento/conduta-1.webp", titulo: "Estratégia alimentar", texto: "O objetivo é um padrão que você consiga manter, não uma dieta para poucas semanas." },
  { img: "/img/emagrecimento/conduta-2.webp", titulo: "Exercício", texto: "Orientação médica alinhada ao seu treino, com foco em preservar e ganhar massa muscular." },
  { img: "/img/emagrecimento/conduta-3.webp", titulo: "Suplementação", texto: "Entra o que tem evidência e indicação no seu caso." },
  { img: "/img/emagrecimento/conduta-4.webp", titulo: "Medicação", texto: "Inclui as canetas quando há indicação. O uso é acompanhado, com atenção à massa muscular e à fase de retirada." },
  { img: "/img/emagrecimento/conduta-5.webp", titulo: "Tratamento da obesidade", texto: "Do sobrepeso ao grau 3. Obesidade é doença crônica e o tratamento segue essa seriedade." },
  { img: "/img/emagrecimento/conduta-6.webp", titulo: "Consequências do emagrecimento", texto: "Flacidez e queda de cabelo podem acompanhar a perda de peso. O ideal é cuidar disso durante o processo." },
];

// faq (Figma 63:172 a 63:213). O Figma escreveu só a resposta 01; as 02 a 05
// foram redigidas a partir da copy aprovada desta mesma página (seções
// "o-que-faz", "dores" e "condutas"), sem inventar fato novo.
export const faq = [
  {
    pergunta: "Esse tratamento é para mim?",
    resposta: "Se você busca redução de gordura e melhora metabólica, sim. Não é indicado para quem procura apenas uma solução rápida, sem mudança de hábitos.",
  },
  {
    pergunta: "Como evitar o efeito sanfona?",
    resposta: "Preservando massa muscular durante a perda de peso e tratando o que trava o seu caso, do sono ao metabolismo. A composição corporal é medida em todas as reavaliações, e a fase de manutenção faz parte do plano.",
  },
  {
    pergunta: "Quais exames fazem parte?",
    resposta: "Não existe pacote pronto. Os exames são solicitados de acordo com o seu caso, a partir da avaliação clínica e da análise de composição corporal.",
  },
  {
    pergunta: "O atendimento é particular?",
    resposta: "Sim, o atendimento é particular, tanto presencial em Bauru quanto por teleconsulta.",
  },
  {
    pergunta: "Funciona por teleconsulta?",
    resposta: "Sim. A avaliação e o acompanhamento acontecem por vídeo, com os exames feitos na sua cidade, para todo o Brasil.",
  },
];

const paragrafosMedico = [
  "O Dr. Thiago é membro do European Board of Obesity Medicine (EBOM) e do Pan American Board of Obesity Medicine (PABOM). Tem três especialidades registradas no CRM.",
  "Na prática, o mesmo médico que conduz o seu emagrecimento entende de treino e de articulação, e mede a composição corporal em todas as reavaliações.",
];


export function Emagrecimento({ artigos }: { artigos: PostResumo[] }) {
  return (
    <>
      <Hero
        trilha={[{ nome: "Home", href: "/" }, { nome: "Tratamentos", href: "/tratamentos/" }, { nome: "Emagrecimento" }]}
        sobretitulo="TRATAMENTO"
        linha1="Tratamento médico"
        linha2="para emagrecimento."
        chamada="Quando emagrecer está difícil, costuma existir um motivo que ainda não foi investigado. O trabalho aqui é encontrar esse motivo, montar um plano realista e acompanhar de perto."
        imagem="/img/emagrecimento/hero.webp"
      />

      <Pilares
        sobretitulo="ANTES DA DIETA, O DIAGNÓSTICO"
        titulo="Primeiro, descobrir o que trava o seu emagrecimento"
        chamada="A consulta investiga por que está difícil emagrecer, com avaliação clínica, análise de composição corporal e exames de acordo com o caso. Com o diagnóstico na mão, o plano deixa de ser palpite."
        itens={pilares}
      />

      <Dores
        sobretitulo="O CICLO DO REGANHO"
        titulo="Por que as tentativas de emagrecimento anteriores não funcionaram"
        chamada="Depois da perda de peso, o corpo aumenta a fome e reduz o gasto de energia. Sem estratégia para essa fase, o reganho é comum. Estes são os fatores mais frequentes:"
        itens={fatores}
      />

      <CardsIlustrado
        sobretitulo="FERRAMENTAS DO TRATAMENTO DE EMAGRECIMENTO"
        titulo="Da mudança de hábitos à medicação, o que o seu caso pedir"
        chamada="O plano combina as frentes que o diagnóstico indicar. Estas são as condutas possíveis:"
        cards={condutas}
      />

      <Medico paragrafos={paragrafosMedico} />
      <Faq titulo="Dúvidas de quem quer emagrecer" itens={faq} />
      <Conteudos titulo="Continue lendo sobre emagrecimento" artigos={artigos} />
      <CtaPratico linha1="Comece seu tratamento de" linha2="emagrecimento. Escolha o formato." />
    </>
  );
}
